/**
 * Journal view component
 * CBT/Stoic/PERMA templates, tags, search, autosave, E2E encryption
 */

'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  BookOpen,
  Search,
  Tag,
  Save,
  Lock,
  LockOpen,
  Edit,
  Trash2,
  X,
  Plus,
  Check,
  AlertCircle,
  Eye,
  Clock,
  FileText,
} from 'lucide-react';
import {
  useJournalEntries,
  useJournalEntry,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useDeleteJournalEntry,
} from '@/lib/hooks/useJournal';
import { getEncryptionStatus, initializeEncryption, getJournalEntry } from '@/lib/services/journal';
import { saveJournalDraft, getJournalDraft, deleteJournalDraft } from '@/lib/storage/indexeddb';
import { isCryptoAvailable } from '@/lib/crypto/e2e';
import {
  formatRelativeTime,
  formatFullDate,
  getWordCount,
  getReadingTime,
  getContentExcerpt,
  generateTitleFromContent,
  cn,
} from '@/lib/utils';

const TEMPLATES = [
  {
    id: 'cbt',
    label: 'CBT Reflection',
    prompt:
      'Situation: What happened?\n\nThoughts: What were you thinking?\n\nFeelings: How did you feel?\n\nBehaviors: What did you do?\n\nAlternative Perspective: How else could you view this?',
  },
  {
    id: 'stoic',
    label: 'Stoic Practice',
    prompt:
      'What is within my control today?\n\nWhat is outside my control?\n\nHow can I practice virtue in this situation?\n\nWhat can I learn from this?',
  },
  {
    id: 'perma',
    label: 'PERMA Wellbeing',
    prompt:
      'Positive Emotions: What brought you joy today?\n\nEngagement: When were you fully absorbed?\n\nRelationships: How did you connect with others?\n\nMeaning: What gave your day purpose?\n\nAccomplishment: What did you achieve?',
  },
];

interface JournalEntryDisplay {
  id: string;
  title: string;
  preview: string;
  content?: string; // Full content for preview
  tags: string[];
  created_at: string;
  updated_at: string;
  is_encrypted?: boolean;
  wordCount?: number;
  readingTime?: number;
}

export function JournalView() {
  const [entries, setEntries] = useState<JournalEntryDisplay[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [encryptionStatus, setEncryptionStatus] = useState<{
    available: boolean;
    enabled: boolean;
  }>({ available: false, enabled: false });
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [autosaveTimer, setAutosaveTimer] = useState<NodeJS.Timeout | null>(null);

  // Queries
  const { data: entriesData, isLoading: entriesLoading } = useJournalEntries(100);
  const { data: editingEntry, isLoading: entryLoading } = useJournalEntry(editingId);
  const { data: viewingEntry, isLoading: viewingLoading } = useJournalEntry(viewingId);
  const createMutation = useCreateJournalEntry();
  const updateMutation = useUpdateJournalEntry();
  const deleteMutation = useDeleteJournalEntry();

  // Check encryption status on mount
  useEffect(() => {
    async function checkEncryption() {
      try {
        const status = await getEncryptionStatus();
        setEncryptionStatus(status);
        setIsEncrypted(status.enabled);
      } catch (err) {
        console.error('Failed to check encryption status:', err);
      }
    }
    checkEncryption();
  }, []);

  // Load entries when data changes
  useEffect(() => {
    if (entriesData) {
      // Load content for each entry to generate previews
      const loadEntriesWithContent = async () => {
        const displayEntries: JournalEntryDisplay[] = await Promise.all(
          entriesData.map(async (entry) => {
            try {
              // Load full entry content to generate preview
              const fullEntry = await getJournalEntry(entry.id);
              const wordCount = getWordCount(fullEntry.content);
              const readingTime = getReadingTime(wordCount);
              const excerpt = getContentExcerpt(fullEntry.content, 150);
              const title = generateTitleFromContent(fullEntry.content);

              return {
                id: entry.id,
                title,
                preview: excerpt,
                content: fullEntry.content, // Store for preview
                tags: entry.tags,
                created_at: entry.created_at,
                updated_at: entry.updated_at,
                is_encrypted: entry.is_encrypted,
                wordCount,
                readingTime,
              };
            } catch (err) {
              // Fallback if loading fails
              const title =
                entry.tags.length > 0
                  ? entry.tags.join(', ')
                  : `Entry from ${new Date(entry.created_at).toLocaleDateString()}`;

              return {
                id: entry.id,
                title,
                preview: `Created ${formatRelativeTime(entry.created_at)}`,
                tags: entry.tags,
                created_at: entry.created_at,
                updated_at: entry.updated_at,
                is_encrypted: entry.is_encrypted,
              };
            }
          })
        );
        setEntries(displayEntries);
      };
      loadEntriesWithContent();
    }
  }, [entriesData]);

  // Load editing entry when selected
  useEffect(() => {
    if (editingEntry) {
      setTitle(editingEntry.tags.join(', ') || 'Untitled');
      setContent(editingEntry.content);
      setTags(editingEntry.tags);
      setIsEncrypted(editingEntry.is_encrypted ?? false);
    }
  }, [editingEntry]);

  // Autosave draft to IndexedDB
  useEffect(() => {
    if (isCreating || editingId) {
      // Clear existing timer
      if (autosaveTimer) {
        clearTimeout(autosaveTimer);
      }

      // Set new timer for autosave
      const timer = setTimeout(async () => {
        const draftId = editingId || 'new-entry';
        try {
          await saveJournalDraft(draftId, content, tags);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
          console.error('Failed to autosave draft:', err);
        }
      }, 2000); // Autosave after 2 seconds of inactivity

      setAutosaveTimer(timer);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [content, tags, isCreating, editingId, autosaveTimer]);

  // Load draft on mount if creating
  useEffect(() => {
    if (isCreating && !editingId) {
      async function loadDraft() {
        try {
          const draft = await getJournalDraft('new-entry');
          if (draft) {
            setContent(draft.content);
            setTags(draft.tags);
          }
        } catch (err) {
          console.error('Failed to load draft:', err);
        }
      }
      loadDraft();
    }
  }, [isCreating, editingId]);

  // Filter entries by search query and tag
  const filteredEntries = useMemo(() => {
    let filtered = entries;

    // Filter by selected tag
    if (selectedTag) {
      filtered = filtered.filter((entry) => entry.tags.includes(selectedTag));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.title.toLowerCase().includes(query) ||
          entry.preview.toLowerCase().includes(query) ||
          (entry.content && entry.content.toLowerCase().includes(query)) ||
          entry.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [entries, searchQuery, selectedTag]);

  // Apply template
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplate(templateId);
    const template = TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setContent(template.prompt);
    }
  }, []);

  // Add tag
  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  }, [tagInput, tags]);

  // Remove tag
  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      setTags(tags.filter((tag) => tag !== tagToRemove));
    },
    [tags]
  );

  // Save entry
  const handleSave = useCallback(async () => {
    if (!content.trim()) {
      setError('Content cannot be empty');
      return;
    }

    setIsSaving(true);
    setSaveStatus('saving');
    setError(null);

    try {
      if (editingId) {
        // Update existing entry
        await updateMutation.mutateAsync({
          entryId: editingId,
          input: {
            content,
            tags,
            encrypt: isEncrypted,
          },
        });
      } else {
        // Create new entry
        await createMutation.mutateAsync({
          content,
          tags,
          encrypt: isEncrypted,
        });
      }

      // Clear draft
      if (editingId) {
        await deleteJournalDraft(editingId);
      } else {
        await deleteJournalDraft('new-entry');
      }

      // Reset form
      setTitle('');
      setContent('');
      setTags([]);
      setSelectedTemplate(null);
      setIsCreating(false);
      setEditingId(null);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry');
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [content, tags, isEncrypted, editingId, createMutation, updateMutation]);

  // Delete entry
  const handleDelete = useCallback(
    async (entryId: string) => {
      if (!confirm('Are you sure you want to delete this entry?')) {
        return;
      }

      try {
        await deleteMutation.mutateAsync(entryId);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete entry');
      }
    },
    [deleteMutation]
  );

  // Start viewing
  const handleView = useCallback((entryId: string) => {
    setViewingId(entryId);
    setEditingId(null);
    setIsCreating(false);
  }, []);

  // Start editing
  const handleEdit = useCallback((entryId: string) => {
    setEditingId(entryId);
    setViewingId(null);
    setIsCreating(false);
  }, []);

  // Close view mode
  const handleCloseView = useCallback(() => {
    setViewingId(null);
  }, []);

  // Handle tag click to filter
  const handleTagClick = useCallback(
    (tag: string) => {
      setSelectedTag(selectedTag === tag ? null : tag);
      setSearchQuery(''); // Clear search when filtering by tag
    },
    [selectedTag]
  );

  // Cancel editing/creating
  const handleCancel = useCallback(async () => {
    // Clear draft
    if (editingId) {
      await deleteJournalDraft(editingId);
    } else if (isCreating) {
      await deleteJournalDraft('new-entry');
    }

    setTitle('');
    setContent('');
    setTags([]);
    setSelectedTemplate(null);
    setIsCreating(false);
    setEditingId(null);
    setViewingId(null);
    setError(null);
  }, [editingId, isCreating]);

  // Initialize encryption
  const handleInitializeEncryption = useCallback(async () => {
    const passphrase = prompt(
      'Enter a passphrase to encrypt your journal entries. This cannot be recovered if forgotten.'
    );
    if (!passphrase) return;

    try {
      await initializeEncryption(passphrase);
      setEncryptionStatus({ available: true, enabled: true });
      setIsEncrypted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize encryption');
    }
  }, []);

  if (entriesLoading || entryLoading || viewingLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading journal...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 max-w-6xl mx-auto">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 break-words">Intelligent Journal</h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            Reflect, learn, and grow with structured templates (CBT, Stoic, PERMA)
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {encryptionStatus.available && (
            <Button
              variant={encryptionStatus.enabled ? 'default' : 'outline'}
              onClick={handleInitializeEncryption}
              disabled={encryptionStatus.enabled}
            >
              {encryptionStatus.enabled ? (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Encrypted
                </>
              ) : (
                <>
                  <LockOpen className="h-4 w-4 mr-2" />
                  Enable Encryption
                </>
              )}
            </Button>
          )}
          <Button onClick={() => setIsCreating(true)}>
            <BookOpen className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>
      </header>

      {/* Encryption Warning */}
      {!encryptionStatus.available && (
        <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Encryption not available
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Your browser does not support Web Crypto API. Journal entries will be stored in
                plaintext.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Search and Tag Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedTag(null); // Clear tag filter when searching
              }}
              className="pl-10"
            />
          </div>
        </div>
        {selectedTag && (
          <Button variant="outline" onClick={() => setSelectedTag(null)} className="shrink-0">
            <X className="h-4 w-4 mr-2" />
            Clear filter: {selectedTag}
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Create/Edit Form */}
      {(isCreating || editingId) && (
        <Card className="p-4 sm:p-6">
          <div className="space-y-4">
            {/* Template Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Template</label>
              <div className="flex gap-2 flex-wrap">
                {TEMPLATES.map((template) => (
                  <Button
                    key={template.id}
                    variant={selectedTemplate === template.id ? 'default' : 'outline'}
                    onClick={() => handleTemplateSelect(template.id)}
                    size="sm"
                  >
                    {template.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium mb-2 block">Content</label>
              <Textarea
                rows={10}
                placeholder="Write your reflection..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="font-mono text-sm min-h-[200px] resize-y"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="text-sm font-medium mb-2 block">Tags</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  className="flex-1"
                />
                <Button onClick={handleAddTag} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {tags.map((tag) => (
                    <div
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-sm"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Encryption Toggle */}
            {encryptionStatus.available && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="encrypt"
                  checked={isEncrypted}
                  onChange={(e) => setIsEncrypted(e.target.checked)}
                  disabled={!encryptionStatus.enabled}
                  className="rounded"
                />
                <label htmlFor="encrypt" className="text-sm font-medium flex items-center gap-2">
                  {isEncrypted ? (
                    <>
                      <Lock className="h-4 w-4" />
                      Encrypt this entry
                    </>
                  ) : (
                    <>
                      <LockOpen className="h-4 w-4" />
                      Encrypt this entry
                    </>
                  )}
                </label>
                {!encryptionStatus.enabled && (
                  <span className="text-xs text-muted-foreground">
                    (Enable encryption in header to use)
                  </span>
                )}
              </div>
            )}

            {/* Save Status - More prominent */}
            {saveStatus !== 'idle' && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-background">
                {saveStatus === 'saving' && (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-sm font-medium">Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      Saved
                    </span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      Save failed
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleSave}
                disabled={isSaving || !content.trim()}
                className="w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : editingId ? 'Update' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* View Mode - Read-only */}
      {viewingId && viewingEntry && !isCreating && !editingId && (
        <Card className="p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold mb-2 break-words">
                  {generateTitleFromContent(viewingEntry.content)}
                </h2>
                {(() => {
                  const wordCount = getWordCount(viewingEntry.content);
                  const readingTime = getReadingTime(wordCount);
                  return (
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <span title={formatFullDate(viewingEntry.created_at)}>
                        {formatRelativeTime(viewingEntry.created_at)}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {wordCount} words
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {readingTime} min read
                      </span>
                      {viewingEntry.is_encrypted && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            Encrypted
                          </span>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
              <Button
                variant="outline"
                onClick={handleCloseView}
                className="w-full sm:w-auto shrink-0"
              >
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>

            {viewingEntry.tags.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {viewingEntry.tags.map((tag) => (
                  <div
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-secondary rounded-full text-xs font-medium"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </div>
                ))}
              </div>
            )}

            <div className="prose prose-sm dark:prose-invert max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-foreground bg-muted/50 p-3 sm:p-4 rounded-lg text-sm sm:text-base overflow-x-auto">
                {viewingEntry.content}
              </pre>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
              <Button onClick={() => handleEdit(viewingId)} className="w-full sm:w-auto">
                <Edit className="h-4 w-4 mr-2" />
                Edit Entry
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmId(viewingId)}
                className="text-destructive hover:text-destructive w-full sm:w-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Entries List */}
      {!isCreating && !editingId && !viewingId && (
        <div className="space-y-4">
          {filteredEntries.length === 0 ? (
            <Card className="p-16 text-center bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <BookOpen className="h-16 w-16 mx-auto mb-6 text-primary/60" />
              <h3 className="text-2xl font-semibold mb-3">
                {searchQuery ? 'No entries found' : 'Start Your Journal'}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {searchQuery
                  ? 'Try a different search query or clear the search to see all entries.'
                  : 'Begin your reflection practice with structured templates. Choose from CBT, Stoic, or PERMA frameworks.'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setIsCreating(true)} size="lg" className="rounded-xl">
                  <BookOpen className="h-5 w-5 mr-2" />
                  Create Your First Entry
                </Button>
              )}
            </Card>
          ) : (
            filteredEntries.map((entry) => (
              <Card
                key={entry.id}
                className="p-4 sm:p-6 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => handleView(entry.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0 mt-0.5">
                        <BookOpen className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-semibold mb-2 line-clamp-2 break-words">
                          {entry.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span title={formatFullDate(entry.created_at)}>
                            {formatRelativeTime(entry.created_at)}
                          </span>
                          {entry.wordCount && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {entry.wordCount} words
                              </span>
                            </>
                          )}
                          {entry.readingTime && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {entry.readingTime} min
                              </span>
                            </>
                          )}
                          {entry.is_encrypted && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Lock className="h-3 w-3" />
                                Encrypted
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {entry.preview && (
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                        {entry.preview}
                      </p>
                    )}
                    {entry.tags.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {entry.tags.map((tag) => (
                          <button
                            key={tag}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTagClick(tag);
                            }}
                            className={cn(
                              'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                              selectedTag === tag
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary hover:bg-secondary/80'
                            )}
                          >
                            <Tag className="h-3 w-3" />
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div
                    className="flex gap-2 shrink-0 justify-end sm:justify-start"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(entry.id)}
                      title="View"
                      className="rounded-lg min-w-[44px] min-h-[44px]"
                      aria-label="View entry"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(entry.id)}
                      title="Edit"
                      className="rounded-lg min-w-[44px] min-h-[44px]"
                      aria-label="Edit entry"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(entry.id)}
                      title="Delete"
                      className="rounded-lg text-muted-foreground hover:text-destructive min-w-[44px] min-h-[44px]"
                      aria-label="Delete entry"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Journal Entry</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
